import { DialogTrigger, Input } from "@light-technology/components";
import { type ReactNode, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { api } from "../../state/api";
import { type Account } from "../../state/api.types";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { emailFieldRules } from "../../utilities/validation";
import { FormModal } from "../shared/FormModal";

type ChangeEmailForm = {
  email: string;
  password: string;
};

type ChangeEmailModalProps = {
  account: Account;
  children: ReactNode;
};

export const ChangeEmailModal = (props: ChangeEmailModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { control, handleSubmit, reset, getValues } = useForm<ChangeEmailForm>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const updateEmailMutation = api.useUpdateEmailMutation();
  const [updateEmail, updateEmailState] = useMutationWithToast(
    updateEmailMutation,
    {
      errorMessage:
        "Your contact details could not be updated at this time. Please try again later.",
      successMessage: () =>
        `A verification email has been sent to ${getValues("email")}. Check your email to complete the update.`,
      onSuccess: () => {
        setIsOpen(false);
      },
    },
  );

  const onSubmit = handleSubmit(({ email, password }) => {
    updateEmail({ email, password });
  });

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {props.children}
      <FormModal
        textTitle="What would you like to change your email to?"
        textCancel="Cancel"
        textSubmit="Update Email"
        isLoading={updateEmailState.isLoading}
        onCancel={handleCancel}
        onSubmit={onSubmit}
        data-ph-capture-attribute-action="update-email"
      >
        <Controller
          control={control}
          rules={emailFieldRules}
          name="email"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="email"
              label="New Email"
              placeholder="Enter your new email"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
        <Controller
          key="password"
          control={control}
          rules={{ required: "Password is required" }}
          name="password"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              className="mt-2xl"
              type="password"
              label="Current Password"
              placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
      </FormModal>
    </DialogTrigger>
  );
};
